import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, tap, retry, ReplaySubject, shareReplay, share } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PopularItem {
  id: string;
  type: 'Destination' | 'Hotel' | 'Category';
  referenceId: number;
  label: string;
  description?: string;
  parentId?: number;
  locationType?: number; // 0=City, 1=Region
  imagePath?: string;
  displayOrder: number;
  propertyCount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PopularService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly baseUrl = environment.baseUrl;

  // ReplaySubject ile son veriyi hatırla (buffer: 1)
  private popularItemsSubject = new ReplaySubject<PopularItem[]>(1);
  private _cachedItems: PopularItem[] = [];
  private isLoading = false;
  private dataLoaded = false;
  private activeRequest$: Observable<PopularItem[]> | null = null;

  // Observable - tüm subscriber'lara aynı veriyi paylaş
  cachedItems$ = this.popularItemsSubject.asObservable();

 
  get cachedItems(): PopularItem[] {
    return this._cachedItems;
  }

  /**
   * Veri yüklü mü?
   */
  get isDataLoaded(): boolean {
    return this.dataLoaded;
  }

  /**
   * Aktif popüler öğeleri getirir
   */
  getActivePopularItems(): Observable<PopularItem[]> {
    // SSR'da çalışmayı engelle
    if (!isPlatformBrowser(this.platformId)) {
      return of([]);
    }

    // Veri zaten yüklendiyse, mevcut cache'i döndür
    if (this.dataLoaded && this._cachedItems.length > 0) {
      return of(this._cachedItems);
    }

    // Önceki yükleme başarısız olduysa tekrar dene
    if (this.dataLoaded && this._cachedItems.length === 0) {
      this.dataLoaded = false;
    }

    // Zaten yükleniyor ise aynı request'i paylaş
    if (this.isLoading && this.activeRequest$) {
      return this.activeRequest$;
    }

    this.isLoading = true;
    const url = `${this.baseUrl}/api/PopularItems/active`;

    this.activeRequest$ = this.http.get<PopularItem[]>(url, { withCredentials: true }).pipe(
      retry({ count: 2, delay: 1000 }),
      tap(response => {
        this._cachedItems = response || [];
        this.dataLoaded = true;
        this.isLoading = false;
        this.activeRequest$ = null;
        this.popularItemsSubject.next(this._cachedItems);
      }),
      catchError(() => {
        this.isLoading = false;
        this.activeRequest$ = null;
        return of([]);
      }),
      shareReplay(1)
    );

    return this.activeRequest$;
  }

  /**
   * Cache'i temizle
   */
  clearCache(): void {
    this._cachedItems = [];
    this.dataLoaded = false;
  }

  /**
   * Resim URL'ini döndürür
   */
  getImageUrl(imagePath?: string): string {
    if (!imagePath) {
      return 'assets/img/destinations/placeholder.jpg';
    }
    return `${this.baseUrl}${imagePath}`;
  }
}
